import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { EmailSectionLayout } from '../../components/EmailSectionLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Users, ListChecks, Send, MailCheck, Plug, ArrowRight, CheckCircle2, AlertTriangle, Loader2, Gauge,
} from 'lucide-react';

const TINTS = {
  indigo: 'bg-indigo-100 text-indigo-600',
  violet: 'bg-violet-100 text-violet-600',
  sky: 'bg-sky-100 text-sky-600',
  emerald: 'bg-emerald-100 text-emerald-600',
};

const StatTile = ({ icon: Icon, label, value, testId, tint = 'indigo' }) => (
  <Card data-testid={testId}>
    <CardContent className="p-5 flex items-center gap-4">
      <div className={`w-11 h-11 rounded-lg ${TINTS[tint]} flex items-center justify-center`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-2xl font-bold text-zinc-900 leading-none">{value}</div>
        <div className="text-sm text-zinc-500 mt-1">{label}</div>
      </div>
    </CardContent>
  </Card>
);

export const EmailDashboardPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [ov, setOv] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get('/email/overview')
      .then((r) => { if (mounted) setOv(r.data); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [api]);

  return (
    <EmailSectionLayout
      actions={
        <Button onClick={() => navigate('/email/campaigns/new')} className="bg-indigo-600 hover:bg-indigo-700" data-testid="email-new-campaign-btn">
          <Send className="w-4 h-4 mr-1.5" /> New campaign
        </Button>
      }
    >
      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>
      ) : (
        <div className="space-y-6">
          {/* Provider status banner */}
          {!ov?.connected ? (
            <Card className="border-amber-200 bg-amber-50" data-testid="email-connect-banner">
              <CardContent className="p-5 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-start gap-3">
                  <Plug className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <div className="font-semibold text-amber-900">Connect SendGrid to start sending</div>
                    <div className="text-sm text-amber-700">Bring your own SendGrid API key — we send through your account for the best deliverability.</div>
                  </div>
                </div>
                <Button onClick={() => navigate('/email/providers')} className="bg-amber-600 hover:bg-amber-700" data-testid="email-connect-cta">
                  Connect provider <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </CardContent>
            </Card>
          ) : !ov?.sender_verified ? (
            <Card className="border-amber-200 bg-amber-50" data-testid="email-verify-banner">
              <CardContent className="p-5 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <div className="font-semibold text-amber-900">Verify your sender email</div>
                    <div className="text-sm text-amber-700">SendGrid won&apos;t deliver until your &quot;from&quot; address is a verified sender. Verify it in SendGrid, then re-check.</div>
                  </div>
                </div>
                <Button variant="outline" onClick={() => navigate('/email/providers')} data-testid="email-verify-cta">Manage provider</Button>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-emerald-200 bg-emerald-50" data-testid="email-ready-banner">
              <CardContent className="p-5 flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                <div className="text-sm text-emerald-800 font-medium">SendGrid connected &amp; sender verified — you&apos;re ready to send.</div>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatTile icon={Users} label="Active subscribers" value={ov?.active_subscribers ?? 0} testId="stat-subscribers" />
            <StatTile icon={ListChecks} label="Lists" value={ov?.lists ?? 0} testId="stat-lists" tint="violet" />
            <StatTile icon={Send} label="Campaigns" value={ov?.campaigns ?? 0} testId="stat-campaigns" tint="sky" />
            <StatTile icon={MailCheck} label="Emails sent (all-time)" value={ov?.emails_sent_total ?? 0} testId="stat-sent" tint="emerald" />
          </div>

          {/* Monthly send usage */}
          <Card data-testid="email-usage-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-sm font-semibold text-zinc-800">
                  <Gauge className="w-4 h-4 text-indigo-600" /> Monthly send usage
                </div>
                <div className="text-sm text-zinc-500">
                  {ov?.send_cap == null
                    ? `${ov?.sends_used_this_month ?? 0} sent · Unlimited`
                    : `${ov?.sends_used_this_month ?? 0} / ${ov?.send_cap} sent`}
                </div>
              </div>
              {ov?.send_cap != null && (
                <div className="w-full h-2.5 rounded-full bg-zinc-100 overflow-hidden">
                  <div
                    className="h-full bg-indigo-600 rounded-full transition-all"
                    style={{ width: `${Math.min(100, ((ov?.sends_used_this_month ?? 0) / Math.max(1, ov.send_cap)) * 100)}%` }}
                  />
                </div>
              )}
              {ov?.send_cap != null && (
                <div className="text-xs text-zinc-400 mt-2">{ov?.sends_remaining} sends remaining this month.</div>
              )}
            </CardContent>
          </Card>

          {/* Quick actions */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <QuickAction title="Add subscribers" desc="Import a CSV or add people manually." onClick={() => navigate('/email/subscribers')} icon={Users} testId="qa-subscribers" />
            <QuickAction title="Create a list" desc="Group subscribers to target a send." onClick={() => navigate('/email/lists')} icon={ListChecks} testId="qa-lists" />
            <QuickAction title="Write a broadcast" desc="Compose & schedule a plain-text email." onClick={() => navigate('/email/campaigns/new')} icon={Send} testId="qa-campaign" />
          </div>
        </div>
      )}
    </EmailSectionLayout>
  );
};

const QuickAction = ({ title, desc, onClick, icon: Icon, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className="text-left p-5 rounded-xl border border-zinc-200 hover:border-indigo-300 hover:shadow-sm transition-all bg-white group"
  >
    <div className="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center mb-3 group-hover:bg-indigo-600 group-hover:text-white transition-colors">
      <Icon className="w-4 h-4" />
    </div>
    <div className="font-semibold text-zinc-900">{title}</div>
    <div className="text-sm text-zinc-500 mt-0.5">{desc}</div>
  </button>
);

export default EmailDashboardPage;
